/**
 * PicksLoadingModal - Animated loading screen for AI pick generation
 * Shows engaging progress phases during the 60-90 second generation process
 */

import { useAuth } from '@contexts/AuthContext';
import type { AnalysisMode } from '@parlay-picker/shared/analysisMode';
import type { PickStreamPhase } from '@parlay-picker/shared/pickStreamEvents';
import {
  useCurrentPhase,
  useGenerationStore,
  usePhaseMessage,
  useStreamingParlays,
} from '@stores/generationStore';
import { type ReactElement, useEffect, useRef, useState } from 'react';

// Minimum time to show modal so users see it was doing something (for fast cached responses)
const MINIMUM_DISPLAY_MS = 300;

interface PicksLoadingModalProps {
  isOpen: boolean;
  analysisMode?: AnalysisMode;
  onDismiss?: () => void; // Allow user to browse while generating
  onCancel?: () => void; // Cancel the generation entirely
  isDismissed?: boolean; // True if user dismissed but generation continues in background
}

interface Phase {
  name: string;
  duration: number; // in seconds
  icon: string;
  messages: string[];
}

// Progress phases with estimated durations
const PHASES: Phase[] = [
  {
    name: 'Fetching Odds',
    duration: 5,
    icon: '📊',
    messages: [
      'Connecting to sportsbook APIs...',
      'Pulling latest betting lines...',
      'Gathering prop markets...',
    ],
  },
  {
    name: 'Loading Stats',
    duration: 8,
    icon: '📈',
    messages: [
      'Fetching player statistics...',
      'Analyzing recent performance...',
      'Checking injury reports...',
    ],
  },
  {
    name: 'AI Analysis',
    duration: 45,
    icon: '🧠',
    messages: [
      'Studying the matchups...',
      'Evaluating historical trends...',
      'Cross-referencing player data...',
      'Calculating probability edges...',
      'Identifying value opportunities...',
      'Building optimal parlays...',
      'Fine-tuning recommendations...',
      'Almost there...',
    ],
  },
  {
    name: 'Building Picks',
    duration: 10,
    icon: '✨',
    messages: ['Organizing recommendations...', 'Finalizing your picks...', 'Preparing results...'],
  },
];

// Extended phases for detailed analysis mode (~95s total)
const PHASES_DETAILED: Phase[] = [
  {
    name: 'Fetching Odds',
    duration: 5,
    icon: '📊',
    messages: [
      'Connecting to sportsbook APIs...',
      'Pulling latest betting lines...',
      'Gathering prop markets...',
    ],
  },
  {
    name: 'Loading Stats',
    duration: 10,
    icon: '📈',
    messages: [
      'Fetching player statistics...',
      'Analyzing recent performance...',
      'Loading head-to-head data...',
      'Checking injury reports...',
    ],
  },
  {
    name: 'Deep Analysis',
    duration: 70,
    icon: '🔬',
    messages: [
      'Running comprehensive analysis...',
      'Studying matchup dynamics...',
      'Evaluating pace and efficiency...',
      'Analyzing defensive schemes...',
      'Checking historical patterns...',
      'Calculating edge probabilities...',
      'Cross-referencing betting trends...',
      'Identifying correlation opportunities...',
      'Evaluating risk factors...',
      'Optimizing leg combinations...',
      'This takes longer for better accuracy...',
      'Building detailed breakdowns...',
      'Almost there...',
    ],
  },
  {
    name: 'Building Picks',
    duration: 10,
    icon: '✨',
    messages: [
      'Compiling detailed analysis...',
      'Organizing recommendations...',
      'Preparing your picks...',
    ],
  },
];

// Premium phases for Opus (~155s total, matches ~150s actual generation time)
const PHASES_PREMIUM: Phase[] = [
  {
    name: 'Fetching Odds',
    duration: 5,
    icon: '📊',
    messages: [
      'Connecting to sportsbook APIs...',
      'Pulling latest betting lines...',
      'Gathering prop markets...',
    ],
  },
  {
    name: 'Loading Stats',
    duration: 10,
    icon: '📈',
    messages: [
      'Fetching player statistics...',
      'Analyzing recent performance...',
      'Loading head-to-head data...',
      'Checking injury reports...',
    ],
  },
  {
    name: 'Premium Analysis',
    duration: 125,
    icon: '🔬',
    messages: [
      'Running premium-tier analysis...',
      'Studying matchup dynamics...',
      'Evaluating pace and efficiency...',
      'Analyzing defensive schemes...',
      'Checking historical patterns...',
      'Calculating edge probabilities...',
      'Cross-referencing betting trends...',
      'Identifying correlation opportunities...',
      'Evaluating risk factors...',
      'Running advanced risk assessment...',
      'Analyzing lineup combinations...',
      'Optimizing leg combinations...',
      'Premium analysis takes a bit longer...',
      'Building detailed breakdowns...',
      'Refining final recommendations...',
      'Almost there...',
    ],
  },
  {
    name: 'Building Picks',
    duration: 15,
    icon: '✨',
    messages: [
      'Compiling premium analysis...',
      'Organizing recommendations...',
      'Finalizing your picks...',
      'Preparing results...',
    ],
  },
];

// Fun basketball facts to display
const BASKETBALL_FACTS = [
  // Records & Milestones
  'LeBron James is the all-time leading scorer with 40,000+ points',
  'Wilt Chamberlain once scored 100 points in a single game',
  'Stephen Curry holds the record for most 3-pointers in a season (402)',
  'Oscar Robertson averaged a triple-double for an entire season in 1961-62',
  'Russell Westbrook holds the career triple-double record with 199',
  'John Stockton has the most career assists (15,806) and steals (3,265)',
  'Wilt Chamberlain averaged 50.4 points per game in 1961-62',
  'Kobe Bryant scored 81 points against the Raptors in 2006',
  'The Lakers and Celtics have combined for 34 NBA championships',
  'Bill Russell won 11 championships in 13 seasons',

  // Game & Rules
  'The NBA three-point line is 23ft 9in from the basket',
  'The shot clock was introduced in 1954 to speed up gameplay',
  'The average NBA game has about 200 possessions',
  'NBA games are 48 minutes long, split into four 12-minute quarters',
  'The 3-point line was added to the NBA in 1979',
  'A regulation NBA basketball weighs 22 ounces',
  'The NBA rim is exactly 10 feet off the ground',
  'An NBA court is 94 feet long and 50 feet wide',
  'The free throw line is 15 feet from the backboard',
  'The paint (key) was widened from 6 to 12 feet in 1951',

  // Physical & Equipment
  'A regulation NBA basketball has 4,118 pebbles on its surface',
  'The average NBA player height is 6\'6"',
  'The average NBA player weighs 215 pounds',
  'Manute Bol and Gheorghe Muresan are the tallest players ever at 7\'7"',
  'Muggsy Bogues is the shortest player ever at 5\'3"',
  'Yao Ming wore size 18 shoes',
  'Shaq wore size 22 shoes, the largest in NBA history',
  'NBA players run an average of 2.5 miles per game',
  'The average NBA vertical leap is about 28 inches',
  "Michael Jordan's vertical leap was measured at 48 inches",

  // History & Firsts
  'The NBA Finals have been held since 1947',
  'The NBA was founded on June 6, 1946, as the BAA',
  'The first NBA game was played on November 1, 1946',
  'Earl Lloyd was the first Black player in NBA history (1950)',
  'The first slam dunk contest was held in 1976 at the ABA All-Star Game',
  'The Dream Team (1992) was the first Olympic team with NBA players',
  'The WNBA was founded in 1996',
  'The NBA-ABA merger happened in 1976',
  'The Minneapolis Lakers won 5 of the first 6 NBA championships',
  'Boston won 8 consecutive championships from 1959-1966',

  // Modern Era
  'LeBron James has played over 65,000 career minutes',
  'Kevin Durant has never averaged below 25 PPG in a healthy season',
  'Giannis Antetokounmpo was the 15th pick in the 2013 draft',
  'Nikola Jokic was the 41st pick in the 2014 draft',
  'Stephen Curry has made 3,700+ career three-pointers',
  'James Harden once had a 32-game streak of 30+ point games',
  'Luka Doncic had 5 triple-doubles in his rookie season',
  'Wemby is the tallest #1 pick in NBA history at 7\'4"',
  'The 2015-16 Warriors went 73-9 in the regular season',
  'The NBA expanded to 30 teams in 2004',

  // Scoring Feats
  'Only 5 players have scored 70+ points in a game',
  "Devin Booker scored 70 points in 2017, the most since Kobe's 81",
  'David Robinson scored 71 points on the last day of 1993-94 to win scoring title',
  'Donovan Mitchell scored 71 points in 2024, the 4th highest ever',
  'The lowest scoring NBA game was 19-18 (Pistons vs Lakers, 1950)',
  'The highest scoring game was 186-184 (Pistons vs Nuggets, 1983)',
  'Wilt Chamberlain had 15 games of 60+ points',
  'Michael Jordan had 5 playoff games of 60+ points',
  'Klay Thompson scored 37 points in a single quarter',
  'Klay Thompson once made 14 three-pointers in a game',

  // Rebounds & Defense
  'Wilt Chamberlain holds the single-game rebound record with 55',
  'Dennis Rodman led the league in rebounds 7 consecutive seasons',
  'Hakeem Olajuwon is the all-time blocks leader with 3,830',
  'Mark Eaton averaged 5.6 blocks per game in 1984-85',
  'Ben Wallace won 4 Defensive Player of the Year awards',
  'The 2003-04 Pistons held opponents to 84.3 PPG in the playoffs',
  'Kawhi Leonard has won 2 Finals MVPs with 2 different teams',
  'Dikembe Mutombo blocked 10+ shots in a game 13 times',
  'Bill Russell is the only player with 50+ rebounds in a playoff game',
  'Dwight Howard led the league in rebounds 5 times',

  // Assists & Playmaking
  'Scott Skiles holds the single-game assist record with 30',
  'Chris Paul has the best assist-to-turnover ratio in history',
  'Magic Johnson averaged 11.2 assists per game for his career',
  'Rajon Rondo had 25 assists in a playoff game',
  'Steve Nash won back-to-back MVPs in 2005 and 2006',
  'Jason Kidd is one of only 3 players with 100+ career triple-doubles',
  'Nikola Jokic led the league in assists as a center in 2022-23',
  'The Showtime Lakers averaged 115+ PPG for multiple seasons',
  'John Wall had 23 assists in a single game',
  'Isaiah Thomas (Pistons) averaged 13.9 assists in 1984-85',

  // Draft & Young Players
  'The youngest player to debut was Andrew Bynum at 18 years old',
  'LeBron James was drafted straight out of high school in 2003',
  'Kobe Bryant was drafted at 17 years old',
  'The draft lottery was introduced in 1985',
  'Tim Duncan was the #1 pick in 1997',
  'Anthony Davis was traded on draft night in 2012',
  'Luka Doncic was traded for Trae Young on draft night',
  'The Pistons drafted Darko Milicic over Carmelo, Wade, and Bosh',
  'Sam Bowie was drafted before Michael Jordan',
  'The Rockets passed on Michael Jordan twice (Hakeem and Bowie)',

  // Championships & Playoffs
  'The 2016 Cavaliers came back from 3-1 in the Finals',
  'Only 4 teams have come back from 0-3 in a playoff series',
  'Michael Jordan is 6-0 in NBA Finals',
  'LeBron James has appeared in 10 NBA Finals',
  'Tim Duncan made the playoffs in 18 of 19 seasons',
  'The Spurs won 5 championships in a 15-year span',
  'The Heat had a 27-game winning streak in 2012-13',
  'The 2017-18 Rockets won 65 games',
  'Dirk Nowitzki won a title at age 32 in 2011',
  'Giannis won Finals MVP in his first Finals appearance',

  // Money & Contracts
  'The NBA salary cap was $3.6 million in 1984',
  'Michael Jordan made $33 million in 1997-98',
  'The league minimum salary is over $1 million',
  'Kevin Garnett was the first $100 million contract',
  'LeBron James has earned over $400 million in NBA salary',
  'The highest paid player makes over $50 million per year',
  'Rookie scale contracts last 4 years',
  'Supermax contracts can be 35% of the salary cap',
  'The NBA revenue sharing began in 2005',
  'The luxury tax was introduced in the 1999 CBA',
];

// Streaming phases (driven by real backend events)
const STREAMING_PHASE_LIST: Array<{ phase: PickStreamPhase; name: string; icon: string }> = [
  { phase: 'fetching_data', name: 'Fetching Odds', icon: '📊' },
  { phase: 'loading_stats', name: 'Loading Stats', icon: '📈' },
  { phase: 'building_prompt', name: 'Building Analysis', icon: '🔬' },
  { phase: 'generating', name: 'AI Generating', icon: '🧠' },
  { phase: 'finalizing', name: 'Scoring & Tiers', icon: '✨' },
];

// Auto-apply checkbox component - uses optimistic local state to avoid race conditions
function AutoApplyCheckbox(): ReactElement {
  const { user: _user, updatePreferences } = useAuth();
  const { autoApplyPicks, setAutoApplyPicks } = useGenerationStore();

  // Use optimistic local state - initialized from store (which syncs from preferences)
  // This prevents the checkbox from flickering when preferences async update completes
  const [localChecked, setLocalChecked] = useState(autoApplyPicks);

  // Sync local state when store value changes (e.g., on initial load from preferences)
  useEffect(() => {
    setLocalChecked(autoApplyPicks);
  }, [autoApplyPicks]);

  const handleChange = (checked: boolean): void => {
    // Update local state immediately (optimistic UI)
    setLocalChecked(checked);
    // Update store for immediate effect in current session
    setAutoApplyPicks(checked);
    // Persist to user preferences (async, may complete later)
    updatePreferences({ autoApplyPicks: checked });
  };

  return (
    <label className="flex items-center gap-2 text-sm text-theme-text-secondary cursor-pointer hover:text-theme-text transition-colors">
      <input
        type="checkbox"
        checked={localChecked}
        onChange={(e) => handleChange(e.target.checked)}
        className="w-4 h-4 rounded border-theme-border bg-theme-surface text-theme-primary focus:ring-theme-primary focus:ring-offset-theme-bg"
      />
      <span>Auto-apply picks when complete</span>
    </label>
  );
}

export default function PicksLoadingModal({
  isOpen,
  analysisMode = 'standard',
  onDismiss,
  onCancel,
  isDismissed = false,
}: PicksLoadingModalProps): ReactElement | null {
  // Streaming state from generation store
  const streamingPhase = useCurrentPhase();
  const streamPhaseMessage = usePhaseMessage();
  const streamingParlays = useStreamingParlays();
  const queuePosition = useGenerationStore((s) => s.queuePosition);
  const queueEstimatedWaitMs = useGenerationStore((s) => s.queueEstimatedWaitMs);
  const isStreamingActive = streamingPhase !== null;
  const isQueued = queuePosition !== null;

  const phases =
    analysisMode === 'premium'
      ? PHASES_PREMIUM
      : analysisMode === 'detailed'
        ? PHASES_DETAILED
        : PHASES;
  const totalDuration = phases.reduce((sum, p) => sum + p.duration, 0);

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [shouldShow, setShouldShow] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [currentFact, setCurrentFact] = useState(
    () => BASKETBALL_FACTS[Math.floor(Math.random() * BASKETBALL_FACTS.length)],
  );
  const openedAtRef = useRef<number>(0);

  // Show modal immediately when opened, reset cancel confirm state
  useEffect(() => {
    if (isOpen) {
      openedAtRef.current = Date.now();
      setShouldShow(true);
      setShowCancelConfirm(false);
    }
  }, [isOpen]);

  // When closing, ensure minimum display duration has elapsed
  useEffect(() => {
    if (!isOpen && shouldShow) {
      const elapsed = Date.now() - openedAtRef.current;
      const remaining = Math.max(0, MINIMUM_DISPLAY_MS - elapsed);

      const timeout = setTimeout(() => {
        setShouldShow(false);
      }, remaining);

      return () => clearTimeout(timeout);
    }
  }, [isOpen, shouldShow]);

  // Increment elapsed time
  useEffect(() => {
    if (!isOpen) {
      setElapsedSeconds(0);
      return;
    }

    const interval = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen]);

  // Rotate facts every 10 seconds
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setCurrentFact(BASKETBALL_FACTS[Math.floor(Math.random() * BASKETBALL_FACTS.length)]);
    }, 10000);

    return () => clearInterval(interval);
  }, [isOpen]);

  // Don't show if closed or user dismissed to browse.
  // Direct isOpen check prevents black screen if shouldShow state gets stuck.
  if (!isOpen || !shouldShow || isDismissed) return null;

  // Calculate current phase and progress
  let accumulatedTime = 0;
  let currentPhaseIndex = 0;
  let phaseProgress = 0;

  for (let i = 0; i < phases.length; i++) {
    if (elapsedSeconds < accumulatedTime + phases[i].duration) {
      currentPhaseIndex = i;
      phaseProgress = (elapsedSeconds - accumulatedTime) / phases[i].duration;
      break;
    }
    accumulatedTime += phases[i].duration;
    if (i === phases.length - 1) {
      currentPhaseIndex = phases.length - 1;
      phaseProgress = 1;
    }
  }

  const currentPhase = phases[currentPhaseIndex];
  const timeBased = Math.min((elapsedSeconds / totalDuration) * 100, 99);

  // Streaming-aware overall progress: smooth time-based fill, floored to current phase position
  // so progress never visually regresses when a new phase arrives
  const streamPhaseIdx = STREAMING_PHASE_LIST.findIndex((s) => s.phase === streamingPhase);
  const phaseFloor = streamPhaseIdx >= 0 ? (streamPhaseIdx / STREAMING_PHASE_LIST.length) * 100 : 0;
  const overallProgress = isStreamingActive
    ? Math.min(Math.max(timeBased, phaseFloor), 99)
    : timeBased;

  // Cycle through messages within the current phase
  const messageIndex = Math.floor(
    (phaseProgress * currentPhase.messages.length) % currentPhase.messages.length,
  );
  const currentMessage =
    currentPhase.messages[Math.min(messageIndex, currentPhase.messages.length - 1)];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-theme-bg border border-theme-border rounded-2xl p-8 max-w-md w-full shadow-2xl my-auto">
        {/* Animated Basketball Icon */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            {/* Outer glow ring */}
            <div className="absolute inset-0 rounded-full bg-theme-primary/20 animate-ping" />
            {/* Basketball container */}
            <div className="relative w-20 h-20 flex items-center justify-center">
              {/* Bouncing basketball */}
              <div className="text-5xl animate-bounce">🏀</div>
            </div>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-center text-theme-text mb-2">
          {analysisMode === 'premium'
            ? 'Running Premium Analysis'
            : analysisMode === 'detailed'
              ? 'Running Deep Analysis'
              : 'Generating Your Picks'}
        </h2>
        <p className="text-theme-text-secondary text-sm text-center mb-6">
          {analysisMode === 'premium'
            ? 'Running premium-tier analysis with advanced risk assessment'
            : analysisMode === 'detailed'
              ? 'Running advanced analysis for detailed breakdown'
              : 'Analyzing matchups and finding value'}
        </p>

        {/* Queue Position Indicator */}
        {isQueued && (
          <div className="mb-6 p-4 bg-theme-primary/10 border border-theme-primary/20 rounded-xl text-center">
            <p className="text-theme-primary font-semibold text-lg mb-1">
              Position #{queuePosition} in queue
            </p>
            <p className="text-theme-text-secondary text-sm">
              Estimated wait: ~{Math.max(1, Math.round((queueEstimatedWaitMs ?? 0) / 60000))} min
            </p>
          </div>
        )}

        {/* Phase Progress — streaming-aware */}
        <div className="space-y-3 mb-6">
          {isStreamingActive
            ? /* Real phases driven by backend events */
              (() => {
                const currentIdx = STREAMING_PHASE_LIST.findIndex(
                  (s) => s.phase === streamingPhase,
                );
                // Derive individual phase bar fill from elapsed time mapped to phase slice
                const sliceSize = 100 / STREAMING_PHASE_LIST.length;
                const sliceStart = currentIdx * sliceSize;
                const phaseBarPct =
                  currentIdx >= 0
                    ? Math.min(Math.max(((timeBased - sliceStart) / sliceSize) * 100, 5), 95)
                    : 50;

                return STREAMING_PHASE_LIST.map((sp, idx) => {
                  const isComplete = idx < currentIdx;
                  const isCurrent = idx === currentIdx;

                  return (
                    <div key={sp.phase} className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-lg transition-all duration-300 ${
                          isComplete
                            ? 'bg-theme-success/30 scale-90'
                            : isCurrent
                              ? 'bg-theme-primary/30 scale-110 animate-pulse'
                              : 'bg-theme-surface/50 scale-90 opacity-50'
                        }`}
                      >
                        {isComplete ? '✓' : sp.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center">
                          <span
                            className={`text-sm font-medium transition-colors ${
                              isComplete
                                ? 'text-theme-success'
                                : isCurrent
                                  ? 'text-theme-text'
                                  : 'text-theme-text-tertiary'
                            }`}
                          >
                            {sp.name}
                          </span>
                          {isCurrent &&
                            sp.phase === 'generating' &&
                            streamingParlays.length > 0 && (
                              <span className="text-xs text-theme-primary font-semibold">
                                {streamingParlays.length} parlay
                                {streamingParlays.length !== 1 ? 's' : ''}
                              </span>
                            )}
                        </div>
                        <div className="h-1 bg-theme-surface rounded-full mt-1 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              isComplete ? 'bg-theme-success' : isCurrent ? 'bg-theme-primary' : ''
                            }`}
                            style={{
                              width: isComplete ? '100%' : isCurrent ? `${phaseBarPct}%` : '0%',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                });
              })()
            : /* Fake time-based phases (fallback for non-streaming) */
              phases.map((phase, idx) => {
                const isComplete = idx < currentPhaseIndex;
                const isCurrent = idx === currentPhaseIndex;
                const isPending = idx > currentPhaseIndex;

                return (
                  <div key={phase.name} className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-lg transition-all duration-300 ${
                        isComplete
                          ? 'bg-theme-success/30 scale-90'
                          : isCurrent
                            ? 'bg-theme-primary/30 scale-110 animate-pulse'
                            : 'bg-theme-surface/50 scale-90 opacity-50'
                      }`}
                    >
                      {isComplete ? '✓' : phase.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <span
                          className={`text-sm font-medium transition-colors ${
                            isComplete
                              ? 'text-theme-success'
                              : isCurrent
                                ? 'text-theme-text'
                                : 'text-theme-text-tertiary'
                          }`}
                        >
                          {phase.name}
                        </span>
                        {isCurrent && (
                          <span className="text-xs text-theme-primary">
                            {Math.round(phaseProgress * 100)}%
                          </span>
                        )}
                      </div>
                      <div className="h-1 bg-theme-surface rounded-full mt-1 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            isComplete
                              ? 'bg-theme-success w-full'
                              : isCurrent
                                ? 'bg-theme-primary'
                                : 'w-0'
                          }`}
                          style={{
                            width: isCurrent
                              ? `${phaseProgress * 100}%`
                              : isPending
                                ? '0%'
                                : '100%',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
        </div>

        {/* Current action message */}
        <div className="bg-theme-surface/50 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-theme-primary rounded-full animate-pulse" />
            <span className="text-sm text-theme-text">
              {isStreamingActive ? streamPhaseMessage : currentMessage}
            </span>
          </div>
          {/* Live parlay counter during streaming generation */}
          {isStreamingActive && streamingPhase === 'generating' && streamingParlays.length > 0 && (
            <div className="mt-2 text-center">
              <span className="text-lg font-bold text-theme-primary">
                {streamingParlays.length}
              </span>
              <span className="text-sm text-theme-text-secondary ml-1">
                parlay{streamingParlays.length !== 1 ? 's' : ''} generated
              </span>
            </div>
          )}
        </div>

        {/* Overall progress */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-theme-text-tertiary mb-1">
            <span>Overall Progress</span>
            <span>{Math.round(overallProgress)}%</span>
          </div>
          <div className="h-2 bg-theme-surface rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-theme-primary via-purple-500 to-theme-primary bg-[length:200%_100%] animate-[shimmer_2s_linear_infinite] rounded-full transition-all duration-1000"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>

        {/* Time elapsed */}
        <div className="text-center text-xs text-theme-text-tertiary mb-4">
          Time elapsed: {Math.floor(elapsedSeconds / 60)}:
          {(elapsedSeconds % 60).toString().padStart(2, '0')}
          {analysisMode === 'premium' && elapsedSeconds > 30 && (
            <span className="ml-2 text-theme-text-tertiary/70">
              (premium analysis takes ~2.5 minutes)
            </span>
          )}
          {analysisMode === 'detailed' && elapsedSeconds > 30 && (
            <span className="ml-2 text-theme-text-tertiary/70">
              (detailed analysis takes ~90 seconds)
            </span>
          )}
        </div>

        {/* Fun fact */}
        <div className="border-t border-theme-border pt-4">
          <p className="text-xs text-theme-text-tertiary text-center">
            <span className="text-theme-text-secondary">Did you know?</span>{' '}
            <span className="text-theme-text-secondary">{currentFact}</span>
          </p>
        </div>

        {/* Auto-apply option + Action buttons */}
        <div className="mt-4 pt-4 border-t border-theme-border">
          {showCancelConfirm ? (
            /* Inline cancel confirmation */
            <div>
              <p className="text-sm text-theme-text-secondary mb-3">
                This still counts toward your daily limit. Any picks already generated will be kept.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCancelConfirm(false)}
                  className="flex-1 py-2.5 rounded-lg bg-theme-surface-hover hover:bg-theme-surface-active text-theme-text text-sm font-medium transition-colors"
                >
                  Keep Generating
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCancelConfirm(false);
                    onCancel?.();
                  }}
                  className="flex-1 py-2.5 rounded-lg bg-theme-danger/20 hover:bg-theme-danger/30 text-theme-danger border border-theme-danger/30 text-sm font-medium transition-colors"
                >
                  Cancel Generation
                </button>
              </div>
            </div>
          ) : (
            /* Normal action buttons */
            <>
              {onDismiss && <AutoApplyCheckbox />}
              <div className="flex gap-3 mt-3">
                {onDismiss && (
                  <button
                    type="button"
                    onClick={onDismiss}
                    className="flex-1 px-4 py-2 bg-theme-surface-hover hover:bg-theme-surface-active text-theme-text rounded-lg text-sm font-medium transition-colors"
                  >
                    Continue Browsing
                  </button>
                )}
                {onCancel && (
                  <button
                    type="button"
                    onClick={() => setShowCancelConfirm(true)}
                    className="flex-1 px-4 py-2 bg-theme-danger/20 hover:bg-theme-danger/30 text-theme-danger border border-theme-danger/30 rounded-lg text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
